/**
 * CreditsGuidePage — iter1118
 *
 * Ръководство за това какво струва всяко AI действие. Чете каталога
 * от `/api/pricing/catalog` и показва всички функции, групирани по
 * сложност. Служи за прозрачност към потребителите и за бърз преглед
 * от админа.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import CreditBadge from "@/components/CreditBadge";
import { Loader2, Info } from "lucide-react";

const COMPLEXITY_LABEL = {
  basic:      "Основни действия",
  medium:     "Средна сложност",
  advanced:   "Задълбочени анализи",
  clinical:   "Клинични разсъждения",
  very_high:  "Най-тежки анализи",
};

const COMPLEXITY_ORDER = ["basic", "medium", "advanced", "clinical", "very_high"];

export default function CreditsGuidePage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pricing/catalog`);
        setFeatures(data.features || {});
      } catch { /* тих провал */ }
      setLoading(false);
    })();
  }, [API]);

  const bg = isDark ? "#0B0F19" : "#F8FAFC";
  const card = isDark ? "#111827" : "#FFFFFF";
  const text = isDark ? "#E2E8F0" : "#0F172A";
  const muted = isDark ? "#94A3B8" : "#475569";
  const border = isDark ? "#1F2937" : "#E2E8F0";

  if (loading || !features) {
    return (
      <div style={{ background: bg, minHeight: "100vh", color: text }}>
        <MobileTopBar />
        <div style={{ padding: 80, display: "flex", justifyContent: "center" }}>
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  // Групиране по сложност
  const grouped = {};
  Object.entries(features).forEach(([fid, f]) => {
    const c = f.complexity || "basic";
    (grouped[c] = grouped[c] || []).push({ id: fid, ...f });
  });

  // Броене за резюмето отгоре
  const totalFeatures = Object.keys(features).length;
  const freeCount = Object.values(features).filter(f => f.credit_cost === 0).length;
  const paidCount = totalFeatures - freeCount;

  return (
    <div style={{ background: bg, minHeight: "100vh", color: text, paddingBottom: 60 }}
         data-testid="credits-guide-page">
      <MobileTopBar />
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 16px" }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
          Какво струват действията с ИИ
        </h1>
        <p style={{ color: muted, fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
          Прозрачно представяне на всяко действие, подпомогнато от изкуствен
          интелект, и колко кредита от баланса Ви изразходва. Безплатните
          действия не намаляват баланса.
        </p>

        {/* Обобщение */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 10, marginBottom: 24,
        }}>
          <SummaryTile card={card} border={border} muted={muted} text={text}
                       label="Общо AI функции" value={totalFeatures} />
          <SummaryTile card={card} border={border} muted={muted} text={text}
                       label="Безплатни" value={freeCount} color="#10B981" />
          <SummaryTile card={card} border={border} muted={muted} text={text}
                       label="С кредити" value={paidCount} color="#F59E0B" />
        </div>

        {/* Таблица по сложност */}
        {COMPLEXITY_ORDER.filter(c => grouped[c]).map((c) => (
          <div key={c} style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>
              {COMPLEXITY_LABEL[c] || c}
            </h2>
            <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: "hidden" }}>
              {grouped[c].map((f, idx) => (
                <div key={f.id}
                     data-testid={`credit-guide-row-${f.id}`}
                     style={{
                       display: "grid",
                       gridTemplateColumns: "1fr auto",
                       alignItems: "center",
                       gap: 12,
                       padding: "12px 14px",
                       borderTop: idx > 0 ? `1px solid ${border}` : "none",
                     }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{f.label_bg}</div>
                    <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>{f.value_bg}</div>
                  </div>
                  <CreditBadge featureId={f.id} fallbackCost={f.credit_cost} variant="default" />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{
          display: "flex", gap: 8, alignItems: "flex-start",
          marginTop: 24, padding: 14, background: card,
          border: `1px solid ${border}`, borderRadius: 10,
          fontSize: 13, color: muted, lineHeight: 1.6,
        }}>
          <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            Балансът Ви се вижда в „Моят профил". Ако имате подарък от лекаря,
            действа дневен лимит, за да не изчерпите всичко наведнъж.
            Безплатните действия и прегледите на вече готови резултати
            НЕ намаляват баланса.
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ card, border, muted, text, label, value, color }) {
  return (
    <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, color: muted, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || text, marginTop: 2 }}>{value}</div>
    </div>
  );
}
